import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronDownIcon,
  FileTextIcon,
  FolderIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  SearchIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Command, CommandEmpty, CommandGroup, CommandList } from "@/components/ui/command";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAuth } from "@/features/auth";
import { resolveSearchMessageContext, resolveSearchRoomLabel } from "@/features/chat/chat.utils";
import {
  useProjectsQuery,
  useWorkspaceBySlugQuery,
} from "@/features/containers/api/containers-hooks";
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ENTER, KEY_ESCAPE } from "@/lib/keyboard-keys";
import { cn } from "@/lib/utils";
import type { Project } from "@shared/types/supabase.types";
import {
  searchDocumentContent,
  searchWorkspaceContainers,
  searchWorkspaceMessages,
} from "../api/search-api";
import type { SearchContainerResult, SearchMessageResult } from "../api/search-api";
import { searchKeys } from "../api/search-keys";
import { WorkspaceSearchItem } from "./workspace-search-item";

interface WorkspaceSearchProps {
  workspaceSlug: string;
  currentProjectSlug?: string;
}

function getMatchingLine(content: string, query: string): string {
  const lower = query.toLowerCase();
  const line = content.split("\n").find((l) => l.toLowerCase().includes(lower));
  return line ?? content.split("\n")[0];
}

export function WorkspaceSearch({ workspaceSlug, currentProjectSlug }: WorkspaceSearchProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [filterToProject, setFilterToProject] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const commandRef = useRef<HTMLDivElement>(null);

  const { data: workspace } = useWorkspaceBySlugQuery(workspaceSlug);
  const { data: projects } = useProjectsQuery(user.id, workspace?.id);

  const projectIds = projects?.map((p) => p.id) ?? [];

  const currentProject =
    currentProjectSlug && filterToProject
      ? (projects?.find((p) => p.slug === currentProjectSlug) ?? null)
      : null;
  const activeProjectIds = currentProject ? [currentProject.id] : projectIds;
  const activeProjectId = currentProject?.id;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const enabled = debouncedQuery.trim().length >= 1 && activeProjectIds.length > 0;

  const { data: containerResults, isFetching: isFetchingContainers } = useQuery({
    queryKey: searchKeys.workspaceContainers(workspaceSlug, debouncedQuery, activeProjectId),
    queryFn: () => searchWorkspaceContainers(activeProjectIds, debouncedQuery),
    enabled,
    staleTime: 30_000,
  });

  const { data: docContentResults, isFetching: isFetchingDocContent } = useQuery({
    queryKey: searchKeys.documentContent(workspaceSlug, debouncedQuery, activeProjectId),
    queryFn: () => searchDocumentContent(activeProjectIds, debouncedQuery),
    enabled,
    staleTime: 30_000,
  });

  const { data: messageResults, isFetching: isFetchingMessages } = useQuery({
    queryKey: searchKeys.workspaceMessages(workspaceSlug, debouncedQuery, activeProjectId),
    queryFn: () => searchWorkspaceMessages(activeProjectIds, debouncedQuery),
    enabled,
    staleTime: 30_000,
  });

  const projectMap = new Map<string, Project>(projects?.map((p) => [p.id, p]) ?? []);

  const matchedProjects =
    debouncedQuery.trim().length >= 1 && !filterToProject
      ? (projects?.filter((p) => p.name.toLowerCase().includes(debouncedQuery.toLowerCase())) ?? [])
      : [];

  const titleDocs = containerResults?.filter((r) => r.container_type === "document") ?? [];

  // Merge content-matched docs — skip any already returned by the title search
  const titleDocIds = new Set(titleDocs.map((d) => d.id));
  const contentOnlyDocs = (docContentResults ?? []).filter((r) => !titleDocIds.has(r.document_id));

  // AI Chats ("room") and Message rooms ("message_room") both open via the shared
  // chats/$chatId route, so they surface together here (CR-25).
  const rooms =
    containerResults?.filter(
      (r) => r.container_type === "room" || r.container_type === "message_room",
    ) ?? [];
  const messages = messageResults ?? [];

  // CR-97: a message room's stored name is empty until a user edits it, so
  // the palette resolves the same default the header/sidebar show ("All
  // project members" when shared) instead of rendering a blank label.
  const roomLabels = {
    allProjectMembers: "All project members",
    untitled: "Untitled message",
    message: "Message",
    chat: "Chat",
  };

  const hasResults =
    matchedProjects.length > 0 ||
    titleDocs.length > 0 ||
    contentOnlyDocs.length > 0 ||
    rooms.length > 0 ||
    messages.length > 0;

  const isFetching = isFetchingContainers || isFetchingDocContent || isFetchingMessages;
  const isOpen = open && debouncedQuery.trim().length >= 1;

  const handleSelectDoc = (item: SearchContainerResult) => {
    const project = item.parent_container_id ? projectMap.get(item.parent_container_id) : null;
    if (!project) return;
    void navigate({
      to: "/workspaces/$slug/projects/$projectSlug/docs/$docId",
      params: {
        slug: workspaceSlug,
        projectSlug: project.slug,
        docId: item.id,
      },
      search: { mentionUserId: undefined },
    });
    setOpen(false);
    setQuery("");
  };

  const handleSelectContentDoc = (docId: string, projectId: string | null) => {
    const project = projectId ? projectMap.get(projectId) : null;
    if (!project) return;
    void navigate({
      to: "/workspaces/$slug/projects/$projectSlug/docs/$docId",
      params: { slug: workspaceSlug, projectSlug: project.slug, docId },
      search: { mentionUserId: undefined },
    });
    setOpen(false);
    setQuery("");
  };

  const handleSelectRoom = (item: SearchContainerResult) => {
    const project = item.parent_container_id ? projectMap.get(item.parent_container_id) : null;
    if (!project) return;
    void navigate({
      to: "/workspaces/$slug/projects/$projectSlug/chats/$chatId",
      params: {
        slug: workspaceSlug,
        projectSlug: project.slug,
        chatId: item.id,
      },
      search: { messageId: undefined, draft: undefined },
    });
    setOpen(false);
    setQuery("");
  };

  const handleSelectMessage = (msg: SearchMessageResult) => {
    const project = msg.room_parent_container_id
      ? projectMap.get(msg.room_parent_container_id)
      : null;
    if (!project) return;
    void navigate({
      to: "/workspaces/$slug/projects/$projectSlug/chats/$chatId",
      params: {
        slug: workspaceSlug,
        projectSlug: project.slug,
        chatId: msg.room,
      },
      search: { messageId: msg.id, draft: undefined },
    });
    setOpen(false);
    setQuery("");
  };

  const handleSelectProject = (project: Project) => {
    void navigate({
      to: "/workspaces/$slug/projects/$projectSlug",
      params: { slug: workspaceSlug, projectSlug: project.slug },
    });
    setOpen(false);
    setQuery("");
  };

  const allDocs = [
    ...titleDocs.slice(0, 6),
    ...contentOnlyDocs.slice(0, Math.max(0, 6 - titleDocs.length)),
  ];

  const toggleSection = (section: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative flex items-center">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute left-3 size-4" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search workspace..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              if (query.trim().length >= 1) setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === KEY_ESCAPE) {
                setOpen(false);
                inputRef.current?.blur();
                return;
              }
              if (e.key === KEY_ARROW_DOWN || e.key === KEY_ARROW_UP || e.key === KEY_ENTER) {
                const cmdkRoot = commandRef.current?.querySelector<HTMLElement>("[cmdk-root]");
                if (cmdkRoot) {
                  cmdkRoot.dispatchEvent(
                    new KeyboardEvent("keydown", {
                      key: e.key,
                      bubbles: true,
                      cancelable: true,
                    }),
                  );
                  e.preventDefault();
                }
              }
            }}
            className="border-input bg-background placeholder:text-muted-foreground h-9 w-72 rounded-md border py-2 pr-3 pl-9 text-sm outline-none focus:border-ring focus:ring-0"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[28rem] max-w-[calc(100vw-2rem)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (inputRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          } else {
            setOpen(false);
          }
        }}
      >
        <div ref={commandRef}>
          <Command shouldFilter={false}>
            <CommandList className="max-h-105">
              {!!currentProjectSlug && (
                <div className="border-b px-3 py-2">
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    spacing={0}
                    value={filterToProject ? "project" : "all"}
                    onValueChange={(val) => {
                      if (val) setFilterToProject(val === "project");
                    }}
                  >
                    <ToggleGroupItem value="all" className="px-3 text-xs">
                      All projects
                    </ToggleGroupItem>
                    <ToggleGroupItem value="project" className="px-3 text-xs">
                      This project
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              )}
              {isFetching && !hasResults ? <CommandEmpty>Searching...</CommandEmpty> : null}
              {!isFetching && !hasResults && debouncedQuery.trim().length >= 1 && (
                <CommandEmpty>No results found.</CommandEmpty>
              )}
              {matchedProjects.length > 0 && (
                <CommandGroup
                  heading={
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-1.5"
                      onClick={() => toggleSection("projects")}
                    >
                      <FolderIcon className="size-3" />
                      Projects
                      <ChevronDownIcon
                        className={cn(
                          "ml-auto size-3 transition-transform",
                          collapsed.has("projects") && "-rotate-90",
                        )}
                      />
                    </button>
                  }
                >
                  {!collapsed.has("projects") &&
                    matchedProjects
                      .slice(0, 5)
                      .map((project) => (
                        <WorkspaceSearchItem
                          key={project.id}
                          label={project.name}
                          query={debouncedQuery}
                          onSelect={() => handleSelectProject(project)}
                        />
                      ))}
                </CommandGroup>
              )}
              {allDocs.length > 0 && (
                <CommandGroup
                  heading={
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-1.5"
                      onClick={() => toggleSection("documents")}
                    >
                      <FileTextIcon className="size-3" />
                      Documents
                      <ChevronDownIcon
                        className={cn(
                          "ml-auto size-3 transition-transform",
                          collapsed.has("documents") && "-rotate-90",
                        )}
                      />
                    </button>
                  }
                >
                  {!collapsed.has("documents") &&
                    allDocs.map((doc) => {
                      const isTitleMatch = "container_type" in doc;
                      if (isTitleMatch) {
                        const project = doc.parent_container_id
                          ? projectMap.get(doc.parent_container_id)
                          : null;
                        return (
                          <WorkspaceSearchItem
                            key={doc.id}
                            context={project?.name}
                            label={doc.name ?? ""}
                            query={debouncedQuery}
                            onSelect={() => handleSelectDoc(doc)}
                          />
                        );
                      }
                      const project = doc.project_id ? projectMap.get(doc.project_id) : null;
                      const context = project
                        ? `${doc.document_name} · ${project.name}`
                        : doc.document_name;
                      const label = doc.matched_line ?? doc.document_name;
                      return (
                        <WorkspaceSearchItem
                          key={doc.document_id}
                          context={context}
                          label={label}
                          query={debouncedQuery}
                          onSelect={() => handleSelectContentDoc(doc.document_id, doc.project_id)}
                        />
                      );
                    })}
                </CommandGroup>
              )}
              {rooms.length > 0 && (
                <CommandGroup
                  heading={
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-1.5"
                      onClick={() => toggleSection("chats")}
                    >
                      <MessageSquareIcon className="size-3" />
                      Chats
                      <ChevronDownIcon
                        className={cn(
                          "ml-auto size-3 transition-transform",
                          collapsed.has("chats") && "-rotate-90",
                        )}
                      />
                    </button>
                  }
                >
                  {!collapsed.has("chats") &&
                    rooms.slice(0, 4).map((room) => {
                      const project = room.parent_container_id
                        ? projectMap.get(room.parent_container_id)
                        : null;
                      return (
                        <WorkspaceSearchItem
                          key={room.id}
                          context={project?.name}
                          label={resolveSearchRoomLabel(room, roomLabels)}
                          query={debouncedQuery}
                          onSelect={() => handleSelectRoom(room)}
                        />
                      );
                    })}
                </CommandGroup>
              )}
              {messages.length > 0 && (
                <CommandGroup
                  heading={
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-1.5"
                      onClick={() => toggleSection("messages")}
                    >
                      <MessageCircleIcon className="size-3" />
                      Messages
                      <ChevronDownIcon
                        className={cn(
                          "ml-auto size-3 transition-transform",
                          collapsed.has("messages") && "-rotate-90",
                        )}
                      />
                    </button>
                  }
                >
                  {!collapsed.has("messages") &&
                    messages
                      .slice(0, 5)
                      .map((msg) => (
                        <WorkspaceSearchItem
                          key={msg.id}
                          context={[resolveSearchMessageContext(msg, roomLabels), msg.full_name]
                            .filter(Boolean)
                            .join(" · ")}
                          label={getMatchingLine(msg.content, debouncedQuery)}
                          query={debouncedQuery}
                          onSelect={() => handleSelectMessage(msg)}
                        />
                      ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
}
